/**
 * The design tokens.
 *
 * These values are the reason the port can claim "identical UI": MUI is themed FROM them
 * rather than bringing its own palette, and every region's CSS reads the same custom
 * properties. One set of values, two consumers.
 *
 * THE OTHER COPY IS `styles/app.css`'s `:root` and `.light`, and it has to exist: MUI needs
 * TypeScript values, CSS needs custom properties, and neither can consume the other. What used
 * to be a comment saying the two must agree is now `tokens.test.ts`, which parses the
 * stylesheet and fails on any drift in either direction.
 */

export type Tokens = {
  bg: string;
  panel: string;
  panel2: string;
  line: string;
  lineStrong: string;
  ink: string;
  inkDim: string;
  inkFaint: string;
  accent: string;
};

/** Dark is the default theme, not the alternate — `.light` overrides it. */
export const DARK: Tokens = {
  bg: '#0e1116',
  panel: '#161b22',
  panel2: '#1c232d',
  line: '#2a3441',
  lineStrong: '#3a4756',
  ink: '#e7edf5',
  inkDim: '#9aa7b6',
  inkFaint: '#5f6c7b',
  accent: '#5b8cff',
};

export const LIGHT: Tokens = {
  bg: '#f4f6fa',
  panel: '#ffffff',
  panel2: '#f0f3f8',
  line: '#dbe1ea',
  lineStrong: '#c2ccd9',
  ink: '#1a212b',
  inkDim: '#586475',
  inkFaint: '#9aa7b6',
  accent: '#5b8cff',
};

/**
 * Colours that do NOT change with the theme.
 *
 * The four documentation-slot colours are ordered (documentation, course, internal, demo)
 * and that order is meaningful — see DOC_KINDS. The seven `STATUS_COLORS` keep their retired
 * vocabulary's NAMES on purpose: they are palette tokens now, referenced across the app (log
 * pills, badges, the about panel's dot) as plain colours, and `PIPELINE_DISPLAY` (lib/docs)
 * picks its seven stage colours from these and `REVIEW_COLORS` below — renaming ~20 CSS
 * custom properties would buy nothing and risk the pixel baselines that photograph them.
 */
export const DOC_COLORS = {
  doc: '#5b8cff', // documentation — blue, order 1 (matches accent)
  course: '#3ec97a', // course — green, order 2
  internal: '#f2a93b', // internal documentation — orange, order 3
  demo: '#a06bff', // demo — purple, order 4 (matches the root-node gradient)
} as const;

export const STATUS_COLORS = {
  not_started: '#6b7684', // grey
  prompt_present: '#7db8e8', // light blue (derived: not_started + a prompt)
  doc_generated: '#5b8cff', // blue
  doc_rejected: '#ef4444', // red
  proofread: '#a06bff', // purple
  approved: '#3ec97a', // green
  needs_update: '#f2a93b', // amber
} as const;

/**
 * The three tokens the doc bar's chip was PHOTOGRAPHED with (Phase 4a's review states —
 * `PIPELINE_DISPLAY`'s draft / in_review / approved keep exactly these, which is what keeps
 * the closed doc bar pixel-identical through the V0.38 unification).
 *
 * Same reasoning as `STATUS_COLORS`: theme-independent, so they are defined once and appear in
 * `:root` only — `cssVars()` produces the same value for DARK and LIGHT, and `tokens.test.ts`
 * derives `.light` as the DIFFERENCE, so a colour that does not change with the theme is
 * automatically absent from it. Adding one of these to `.light` by hand fails that test.
 *
 * `in_review` and `approved` deliberately reuse the amber and green `STATUS_COLORS` already
 * hold — the colours mean the same thing to a reader, and the heatmap (V0.39) is specified to
 * reuse them again. `draft` is its OWN slate, one step lighter than `not_started`'s, because
 * a 6px dot at that grey is invisible on the dark panel.
 */
export const REVIEW_COLORS = {
  draft: '#8b95a3', // slate — written, unread
  in_review: '#f2a93b', // amber — waiting for somebody
  approved: '#3ec97a', // green — a person has read this text
} as const;

export const RADIUS = 12;

/**
 * Font stacks. Both families still fall back to system-ui, which is what makes the app
 * survive with no network today — Offline Phase 1 self-hosts the two web fonts rather
 * than adding a hard dependency on them.
 */
export const FONT_DISPLAY = "'Space Grotesk',system-ui,sans-serif";
export const FONT_BODY = "'Inter',system-ui,sans-serif";

/** The tokens as CSS custom properties, so components can use var(--ink) as they do today. */
export function cssVars(t: Tokens): Record<string, string> {
  return {
    '--bg': t.bg,
    '--panel': t.panel,
    '--panel-2': t.panel2,
    '--line': t.line,
    '--line-strong': t.lineStrong,
    '--ink': t.ink,
    '--ink-dim': t.inkDim,
    '--ink-faint': t.inkFaint,
    '--accent': t.accent,
    '--doc-color': DOC_COLORS.doc,
    '--course-color': DOC_COLORS.course,
    '--internal-color': DOC_COLORS.internal,
    '--demo-color': DOC_COLORS.demo,
    '--status-not-started': STATUS_COLORS.not_started,
    '--status-prompt-present': STATUS_COLORS.prompt_present,
    '--status-doc-generated': STATUS_COLORS.doc_generated,
    '--status-doc-rejected': STATUS_COLORS.doc_rejected,
    '--status-proofread': STATUS_COLORS.proofread,
    '--status-approved': STATUS_COLORS.approved,
    '--status-needs-update': STATUS_COLORS.needs_update,
    '--review-draft': REVIEW_COLORS.draft,
    '--review-in-review': REVIEW_COLORS.in_review,
    '--review-approved': REVIEW_COLORS.approved,
    '--radius': `${RADIUS}px`,
    '--font-display': FONT_DISPLAY,
    '--font': FONT_BODY,
  };
}
